import React, { useState, useCallback, useEffect } from 'react';

/**
 * ViewerChrome — the audience-facing frame around a presentation.
 *
 * Three modes share one engine today, and only two of them were ever designed:
 * the editor (full toolbar) and the embed (no UI at all). The third — someone opening
 * a shared link — fell back to the editor chrome, which showed strangers Save/Edit/
 * Settings and a hamburger full of developer tools (audit, Content post 4437).
 *
 * This is that missing third mode: title, progress, fullscreen and share — nothing
 * that could modify the presentation.
 *
 * Activated with `?view=1`; the share dialog produces exactly that URL.
 */

interface ViewerChromeProps {
    title?: string;
    currentPOI: number;
    totalPOIs: number;
    /** Presentation id for the share/embed links; falls back to the current URL. */
    presentationId?: string | number | null;
}

const shell: React.CSSProperties = {
    position: 'absolute',
    zIndex: 1200,
    color: '#fff',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
};

const pill: React.CSSProperties = {
    background: 'rgba(15,17,26,0.72)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 12,
    padding: '8px 14px',
    fontSize: 13,
    lineHeight: 1.3,
};

const iconBtn: React.CSSProperties = {
    ...pill,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    userSelect: 'none',
};

export const ViewerChrome: React.FC<ViewerChromeProps> = ({ title, currentPOI, totalPOIs, presentationId }) => {
    const [shareOpen, setShareOpen] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
        else document.documentElement.requestFullscreen().catch(() => {});
    }, []);

    /** Share URL = current location, forced into viewer mode and stripped of editor params. */
    const buildUrl = useCallback((mode: 'view' | 'embed'): string => {
        try {
            const u = new URL(window.location.href);
            for (const p of ['landing', 'motion', 'nopins', 'debug', 'export']) u.searchParams.delete(p);
            u.searchParams.delete(mode === 'view' ? 'embed' : 'view');
            u.searchParams.set(mode, '1');
            if (presentationId && !u.searchParams.get('pres')) u.searchParams.set('pres', String(presentationId));
            return u.toString();
        } catch {
            return window.location.href;
        }
    }, [presentationId]);

    const copy = useCallback(async (text: string, tag: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Clipboard API needs a secure context; fall back to a temporary textarea.
            const ta = document.createElement('textarea');
            ta.value = text; document.body.appendChild(ta); ta.select();
            try { document.execCommand('copy'); } catch { /* nothing else we can do */ }
            document.body.removeChild(ta);
        }
        setCopied(tag);
        setTimeout(() => setCopied(null), 1800);
    }, []);

    const viewUrl = buildUrl('view');
    const embedSnippet = `<iframe src="${buildUrl('embed')}" width="100%" height="600" style="border:0" allow="fullscreen; xr-spatial-tracking"></iframe>`;

    return (
        <>
            {title && (
                // Never run under the counter/share/fullscreen group on narrow screens (#1763 finding).
                <div style={{ ...shell, top: 16, left: 16, maxWidth: 'min(52vw, calc(100vw - 268px))' }}>
                    <div style={{ ...pill, fontWeight: 600, letterSpacing: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {title}
                    </div>
                </div>
            )}

            <div style={{ ...shell, top: 16, right: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                {totalPOIs > 0 && (
                    <div style={{ ...pill, fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
                        {Math.min(currentPOI + 1, totalPOIs)} / {totalPOIs}
                    </div>
                )}
                <div style={iconBtn} onClick={() => setShareOpen(v => !v)} title="Teilen">
                    <span style={{ fontSize: 15 }}>⤴</span> Teilen
                </div>
                <div style={iconBtn} onClick={toggleFullscreen} title="Vollbild">
                    {isFullscreen ? '⤡' : '⤢'}
                </div>
            </div>

            {shareOpen && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={() => setShareOpen(false)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: 'min(560px, 100%)', background: '#141822', color: '#fff',
                            border: '1px solid rgba(255,255,255,0.16)', borderRadius: 16, padding: 22,
                            fontFamily: 'system-ui, -apple-system, sans-serif', boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
                        }}
                    >
                        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Präsentation teilen</div>
                        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 18 }}>
                            Der Link öffnet die Präsentation in der Zuschaueransicht — ohne Bearbeitungswerkzeuge.
                        </div>

                        <label style={{ fontSize: 12, opacity: 0.65, display: 'block', marginBottom: 6 }}>Link</label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                            <input
                                readOnly value={viewUrl}
                                onFocus={e => e.currentTarget.select()}
                                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 9, padding: '10px 12px', color: '#fff', fontSize: 13, minWidth: 0 }}
                            />
                            <button
                                onClick={() => copy(viewUrl, 'link')}
                                style={{ background: copied === 'link' ? '#2e9e5b' : '#3b82f6', color: '#fff', border: 0, borderRadius: 9, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                                {copied === 'link' ? 'Kopiert' : 'Kopieren'}
                            </button>
                        </div>

                        <label style={{ fontSize: 12, opacity: 0.65, display: 'block', marginBottom: 6 }}>Auf einer Website einbetten</label>
                        <textarea
                            readOnly value={embedSnippet} rows={3}
                            onFocus={e => e.currentTarget.select()}
                            style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 9, padding: '10px 12px', color: '#fff', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', resize: 'vertical' }}
                        />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button
                                onClick={() => copy(embedSnippet, 'embed')}
                                style={{ background: copied === 'embed' ? '#2e9e5b' : 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 9, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}
                            >
                                {copied === 'embed' ? 'Kopiert' : 'Code kopieren'}
                            </button>
                            <button
                                onClick={() => setShareOpen(false)}
                                style={{ background: 'transparent', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 9, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewerChrome;
